import React, { Fragment } from "react";
import { InfoCircleTwoTone } from "@ant-design/icons";
import { Table } from "antd";
import { useNavigate } from "react-router-dom";
import moment from "moment";

/** Same idea as `logisticAllTab`: map stored order to Ant Design `sortOrder` (supports ASC/DESC). */
const getSortOrder = (tableSorter, columnKey) => {
	if (tableSorter.field !== columnKey) {
		return null;
	}
	const o = String(tableSorter.order || "").toUpperCase();
	if (o === "ASC") {
		return "ascend";
	}
	if (o === "DESC") {
		return "descend";
	}
	return null;
};

/**
 * Server-sorted table: no local reordering of rows.
 * Use object form (not a bare function) so Ant Design's sort cycle stays reliable with `sortDirections`.
 */
const SERVER_SORTER = { compare: (_a, _b) => 0 };

const getCellValue = (record, keys) => {
	for (const key of keys) {
		const value = record?.[key];
		if (value !== undefined && value !== null && value !== "") {
			return value;
		}
	}

	return "-";
};

const formatCreatedOnDate = (value) => {
	if (!value || value === "-") {
		return "-";
	}

	const parsedDate = moment(value, ["M/D/YYYY h.mm.ss A", "M/D/YYYY h:mm:ss A", moment.ISO_8601, "YYYY-MM-DD HH:mm:ss"], true);
	return parsedDate.isValid() ? parsedDate.format("DD/MM/YYYY") : "-";
};

const formatRecordCount = (total, range) => {
	if (!total) {
		return "Showing 0 records";
	}

	return `Showing ${range[0]}-${range[1]} of ${total.toLocaleString()} records`;
};

const KycMaterialsTab = ({
	tabTableData,
	isLoading,
	tableSorter,
	OnhandleTableSortColumn,
	emptyText = "No data found",
	navigationContext = {},
}) => {
	const navigate = useNavigate();
	const totalRecords = Number(tabTableData.totalRecords || 0);
	const currentPage = Number(tabTableData.currentPage || 1);
	const pageSize = Number(tabTableData.pageSize || 10);

	const handleMaterialCharacteristicsClick = (event, record) => {
		event.stopPropagation();
		const name = getCellValue(record, ["ipsl_name", "ispl_name", "ispl_Name", "materialName", "ispl_product"]);
		if (!name || name === "-") {
			return;
		}

		const partnerCrmGuid = String(navigationContext.partnerCrmGuid || "").trim();
		const hasDashboardDrillContext = Boolean(
			String(navigationContext.materialsIsplCustomer || "").trim() ||
				String(navigationContext.selectedAccountName || "").trim() ||
				String(navigationContext.groupCompanyName || "").trim() ||
				String(navigationContext.groupCompanyId || "").trim()
		);

		let materialsCustomer = "";
		let accountName = "";
		let returnTab = "materials";

		if (partnerCrmGuid) {
			accountName =
				String(navigationContext.selectedAccountName || "").trim() ||
				String(navigationContext.materialsIsplCustomer || "").trim();
			materialsCustomer = accountName;
			returnTab = navigationContext.returnTab || "account";
		} else if (hasDashboardDrillContext) {
			const recordCustomer = getCellValue(record, ["ispl_customer", "ispl_Customer"]);
			materialsCustomer =
				String(navigationContext.materialsIsplCustomer || "").trim() ||
				(recordCustomer && recordCustomer !== "-" ? String(recordCustomer).trim() : "");
			accountName =
				String(navigationContext.selectedAccountName || "").trim() || materialsCustomer;
			returnTab = "materials";
		}

		navigate(`/kyc/material-characteristics/${encodeURIComponent(name)}`, {
			state: {
				materialName: name,
				groupCompanyId:
					partnerCrmGuid || hasDashboardDrillContext
						? String(navigationContext.groupCompanyId || "").trim()
						: "",
				groupCompanyName:
					partnerCrmGuid || hasDashboardDrillContext
						? String(navigationContext.groupCompanyName || "").trim()
						: "",
				materialsCustomer,
				accountName,
				returnTab,
				partnerCrmGuid,
			},
		});
	};

	const handleTableAscDes = (pagination, filters, sorter) => {
		const s = Array.isArray(sorter) ? sorter[0] || {} : sorter || {};
		const sortOrder = s.order;
		const rawField = s.field;
		const sortField =
			rawField !== undefined && rawField !== null && rawField !== ""
				? Array.isArray(rawField)
					? rawField[0]
					: rawField
				: tableSorter.field;

		let nextOrder = tableSorter.order;
		if (sortOrder === "ascend") {
			nextOrder = "ASC";
		} else if (sortOrder === "descend") {
			nextOrder = "DESC";
		} else {
			const pageChanged =
				pagination.current !== tabTableData.currentPage ||
				(pagination.pageSize != null &&
					tabTableData.pageSize != null &&
					pagination.pageSize !== tabTableData.pageSize);
			if (!pageChanged && String(sortField) === String(tableSorter.field)) {
				const o = String(tableSorter.order || "").toUpperCase();
				nextOrder = o === "DESC" ? "ASC" : "DESC";
			}
		}

		OnhandleTableSortColumn({
			sortColumn: sortField,
			orderBy: nextOrder,
			currentPage: pagination.current,
			pageSize: pagination.pageSize,
		});
	};

	const tableColumns = [
		{
			title: "Material Name",
			dataIndex: "ipsl_name",
			key: "ipsl_name",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 260,
			sortOrder: getSortOrder(tableSorter, "ipsl_name"),
			render: (_, record) => (
				<span>{getCellValue(record, ["ipsl_name", "ispl_name", "ispl_Name", "materialName"])}</span>
			),
		},
		{
			title: "SAP Code",
			dataIndex: "ispl_materialcode",
			key: "ispl_materialcode",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 140,
			sortOrder: getSortOrder(tableSorter, "ispl_materialcode"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_materialcode", "ispl_Materialcode"])}</span>,
		},
		{
			title: "CRM Code",
			dataIndex: "ispl_crmcode",
			key: "ispl_crmcode",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 140,
			sortOrder: getSortOrder(tableSorter, "ispl_crmcode"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_crmcode", "ispl_CRMCode"])}</span>,
		},
		{
			title: "Material Type",
			dataIndex: "ispl_materialtype",
			key: "ispl_materialtype",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "ispl_materialtype"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_materialtype", "ispl_MaterialType"])}</span>,
		},
		{
			title: "Customer (Buss. Partner)",
			dataIndex: "ispl_customer",
			key: "ispl_customer",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "ispl_customer"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_customer", "ispl_Customer"])}</span>,
		},
		{
			title: "Customer Business Category",
			dataIndex: "ispl_customerbusinesscategory",
			key: "ispl_customerbusinesscategory",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "ispl_customerbusinesscategory"),
			render: (_, record) => (
				<span>{getCellValue(record, ["ispl_customerbusinesscategory", "ispl_CustomerBusinessCategory"])}</span>
			),
		},
		{
			title: "Customer Business Sub Category",
			dataIndex: "ispl_customerbusinesssubcategory",
			key: "ispl_customerbusinesssubcategory",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 240,
			sortOrder: getSortOrder(tableSorter, "ispl_customerbusinesssubcategory"),
			render: (_, record) => (
				<span>
					{getCellValue(record, ["ispl_customerbusinesssubcategory", "ispl_CustomerBusinessSubCategory"])}
				</span>
			),
		},
		{
			title: "Gross Weight",
			dataIndex: "ispl_grossweight",
			key: "ispl_grossweight",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 120,
			sortOrder: getSortOrder(tableSorter, "ispl_grossweight"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_grossweight", "ispl_GrossWeight"])}</span>,
		},
		{
			title: "Net Weight",
			dataIndex: "ispl_netweight",
			key: "ispl_netweight",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 110,
			sortOrder: getSortOrder(tableSorter, "ispl_netweight"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_netweight", "ispl_NetWeight"])}</span>,
		},
		{
			title: "HSN No.",
			dataIndex: "ispl_hsnno",
			key: "ispl_hsnno",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 120,
			sortOrder: getSortOrder(tableSorter, "ispl_hsnno"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_hsnno", "ispl_HSNNo"])}</span>,
		},
		{
			title: "Sales Unit",
			dataIndex: "ispl_salesunit",
			key: "ispl_salesunit",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 110,
			sortOrder: getSortOrder(tableSorter, "ispl_salesunit"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_salesunit", "ispl_SalesUnit"])}</span>,
		},
		{
			title: "Base Unit",
			dataIndex: "ispl_unit",
			key: "ispl_unit",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 100,
			sortOrder: getSortOrder(tableSorter, "ispl_unit"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_unit", "ispl_Unit"])}</span>,
		},
		{
			title: "Conversion",
			dataIndex: "ispl_conversion",
			key: "ispl_conversion",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 110,
			sortOrder: getSortOrder(tableSorter, "ispl_conversion"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_conversion", "ispl_Conversion"])}</span>,
		},
		{
			title: "Production Completion Days",
			dataIndex: "ispl_productioncompletiondays",
			key: "ispl_productioncompletiondays",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 200,
			sortOrder: getSortOrder(tableSorter, "ispl_productioncompletiondays"),
			render: (_, record) => (
				<span>{getCellValue(record, ["ispl_productioncompletiondays", "ispl_ProductionCompletionDays"])}</span>
			),
		},
		{
			title: "Distribution Channel",
			dataIndex: "ispl_distributionchannel",
			key: "ispl_distributionchannel",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "ispl_distributionchannel"),
			render: (_, record) => (
				<span>{getCellValue(record, ["ispl_distributionchannel", "ispl_DistributionChannel"])}</span>
			),
		},
		{
			title: "Created On",
			dataIndex: "createdon",
			key: "createdon",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 120,
			sortOrder: getSortOrder(tableSorter, "createdon"),
			render: (_, record) => {
				const raw = getCellValue(record, ["createdon", "createdOn", "createdAt"]);
				return <span>{formatCreatedOnDate(raw)}</span>;
			},
		},
		{
			title: "Owner",
			dataIndex: "ownerid",
			key: "ownerid",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "ownerid"),
			render: (_, record) => <span>{getCellValue(record, ["ownerid", "ownerId", "OwnerID"])}</span>,
		},
		{
			title: "Action",
			key: "action",
			width: 90,
			fixed: "right",
			render: (_, record) => (
				<div
					className="tbl_info_details"
					onClick={(event) => handleMaterialCharacteristicsClick(event, record)}
					role="presentation"
				>
					<InfoCircleTwoTone />
				</div>
			),
		},
	];

	return (
		<Fragment>
			<Table
				bordered
				scroll={{ x: 3200 }}
				className="logistic_tables kyc_logistic_tables"
				dataSource={tabTableData.rows}
				columns={tableColumns}
				sortDirections={["ascend", "descend"]}
				onChange={handleTableAscDes}
				rowKey={(record, index) => record.id || record.materialRowId || index}
				locale={{ emptyText }}
				pagination={{
					total: totalRecords,
					current: currentPage,
					pageSize,
					responsive: true,
					showSizeChanger: false,
					showTotal: formatRecordCount,
				}}
				loading={isLoading}
			/>
		</Fragment>
	);
};

export default KycMaterialsTab;
